import { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { supabase } from '../supabase.js';
import { useAuth } from '../AuthContext.jsx';
import { useClientPath } from '../lib/clientLink.js';
import { NAV, ASK_LINE, LEARN_LINE, groupForPath, isEntitled, learnPath } from '../modules/index.js';
import { priceLine } from '../learn/copy.js';
import ClientSwitcher from './ClientSwitcher.jsx';

// Session 8 Part 4: collapsible groups, Inbox first for everyone, locked
// items with a padlock and a popover instead of a page, Settings at the
// bottom for the agency. Open/closed state per group is remembered in
// localStorage as lotus.nav.<group>; the group holding the current page is
// always open on load. Agency users see exactly what the selected client sees.

const SVG = (paths) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {paths}
  </svg>
);
const ICON = {
  inbox: SVG(<><path d="M3 13l2.5-7A2 2 0 0 1 7.4 5h9.2a2 2 0 0 1 1.9 1l2.5 7v5a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-5z" /><path d="M3 13h5l1.5 2h5L16 13h5" /></>),
  blogs: SVG(<><path d="M5 4h11a3 3 0 0 1 3 3v13H8a3 3 0 0 1-3-3V4z" /><path d="M9 8h6M9 12h6M9 16h4" /></>),
  course: SVG(<><path d="M4 6l8-3 8 3-8 3-8-3z" /><path d="M4 6v6c0 2 4 4 8 4s8-2 8-4V6" /><path d="M20 9v5" /></>),
  audit: SVG(<><path d="M12 2v3M12 19v3M4.2 4.2l2.1 2.1M17.7 17.7l2.1 2.1M2 12h3M19 12h3M4.2 19.8l2.1-2.1M17.7 6.3l2.1-2.1" /><circle cx="12" cy="12" r="4" /></>),
  analytics: SVG(<path d="M4 20V10M10 20V4M16 20v-8M22 20H2" />),
  // Session 49: an envelope for Newsletters
  newsletter: SVG(<><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 7l9 6 9-6" /></>),
  settings: SVG(<><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" /></>),
};
// Simple inline padlock, no dependency.
const LOCK = (
  <svg className="lock-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="4" y="11" width="16" height="10" rx="2" />
    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
  </svg>
);

const NAV_KEY = (group) => `lotus.nav.${group}`;
function readOpen(group) {
  try {
    const v = window.localStorage.getItem(NAV_KEY(group));
    return v === null ? true : v === 'open';
  } catch { return true; }
}
function writeOpen(group, open) {
  try { window.localStorage.setItem(NAV_KEY(group), open ? 'open' : 'closed'); } catch { /* private mode etc. */ }
}

export default function LeftRail() {
  const location = useLocation();
  const { me, isAgency, signOut, activeClient, activeId, entitlements, inboxAll, inboxTick } = useAuth();
  const [inboxCount, setInboxCount] = useState(null);
  const [open, setOpen] = useState(false);               // mobile drawer
  const [groups, setGroups] = useState(() => Object.fromEntries(
    NAV.filter((n) => n.kind === 'group').map((g) => [g.key, readOpen(g.key)]),
  ));
  const [lockOpen, setLockOpen] = useState(null);        // key of the locked entry whose popover is showing
  // Session 49: the catalogue, once, so a locked entry can say its price. The
  // price list is public (the sign-up page reads it with no session).
  const [plans, setPlans] = useState([]);
  useEffect(() => {
    let cancelled = false;
    supabase.from('v_portal_plans').select('group_code, plan_code, sellable, price_monthly_cents, requires_plan_code')
      .then(({ data }) => { if (!cancelled) setPlans(data || []); });
    return () => { cancelled = true; };
  }, []);
  const plansOfGroup = (g) => plans.filter((p) => p.group_code === g);

  // Close the mobile drawer and any popover on route change; open the group
  // that holds the current page.
  useEffect(() => {
    setOpen(false); setLockOpen(null);
    const g = groupForPath(location.pathname);
    if (g) setGroups((s) => (s[g] ? s : { ...s, [g]: true }));
  }, [location.pathname]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!activeId) { setInboxCount(null); return; }
      // Session 8 (B1): only actionable rows count. Session 14: read alerts
      // (is_read = true) don't count, and the agency counts the same scope
      // the Inbox shows: the switcher's client, or every client in
      // "All clients" mode. A client counts their own rows.
      let q = supabase
        .from('v_portal_attention')
        .select('*', { count: 'exact', head: true })
        .eq('actionable', true)
        .not('is_read', 'is', true);
      q = isAgency
        ? (inboxAll ? q.eq('audience', 'agency') : q.eq('audience', 'agency').eq('client_id', activeId))
        : q.eq('client_id', activeId).eq('audience', 'client');
      const { count, error } = await q;
      if (cancelled) return;
      setInboxCount(error ? null : count);
    })();
    return () => { cancelled = true; };
  }, [activeId, isAgency, inboxAll, inboxTick, location.pathname]);

  const toggleGroup = (key) => setGroups((s) => {
    const next = !s[key];
    writeOpen(key, next);
    return { ...s, [key]: next };
  });

  return (
    <>
      <button
        type="button"
        className="rail-toggle"
        aria-label="Open navigation"
        onClick={() => setOpen((v) => !v)}
      >
        ☰ Menu
      </button>
      <div className={`rail-backdrop ${open ? 'open' : ''}`} onClick={() => setOpen(false)} />
      <aside className={`rail ${open ? 'open' : ''}`} aria-label="Portal navigation">
        <div className="rail-logo">
          <img src="/dl-logo.png" alt="Digital Lotus" onError={(e) => { e.currentTarget.replaceWith(Object.assign(document.createElement('span'), { className: 'wordmark', innerText: 'Digital Lotus' })); }} />
        </div>

        {isAgency && (
          <div className="rail-switcher">
            <ClientSwitcher />
          </div>
        )}

        <nav>
          {NAV.map((entry) => {
            const entitled = isEntitled(entry, entitlements);
            if (entry.kind === 'item') {
              return (
                <div key={entry.key} className="rail-module">
                  {entitled ? (
                    <RailLink to={entry.path} exact={entry.exact} icon={entry.icon} label={entry.label}
                              badge={entry.key === 'inbox' ? inboxCount : null} onNavigate={() => setOpen(false)} />
                  ) : (
                    <LockedEntry entry={entry} icon={entry.icon} open={lockOpen === entry.key}
                                 learn={learnPath(entry)} price={priceLine(plansOfGroup(entry.group))}
                                 isAgency={isAgency} activeId={activeId}
                                 onToggle={() => setLockOpen((k) => (k === entry.key ? null : entry.key))}
                                 onClose={() => setLockOpen(null)} />
                  )}
                </div>
              );
            }
            const isOpen = !!groups[entry.key];
            return (
              <div key={entry.key} className={`rail-module rail-group ${isOpen ? 'open' : ''} ${entitled ? '' : 'locked'}`}>
                <button type="button" className="rail-module-header rail-group-toggle" aria-expanded={isOpen}
                        aria-controls={`nav-group-${entry.key}`} onClick={() => toggleGroup(entry.key)}>
                  <span className="rail-icon">{ICON[entry.icon]}</span>
                  <span className="rail-label">{entry.label}</span>
                  {!entitled && LOCK}
                  <span className="chevron" aria-hidden="true">{isOpen ? '▾' : '▸'}</span>
                </button>
                {isOpen && (
                  <div className="sub" id={`nav-group-${entry.key}`}>
                    {!entitled && entry.description && (
                      <LockedEntry entry={entry} open={lockOpen === entry.key} labelOverride={`About ${entry.label}`}
                                   learn={learnPath(entry)} price={priceLine(plansOfGroup(entry.group))}
                                   isAgency={isAgency} activeId={activeId}
                                   onToggle={() => setLockOpen((k) => (k === entry.key ? null : entry.key))}
                                   onClose={() => setLockOpen(null)} />
                    )}
                    {entry.items.filter((it) => !(it.hideWhenLocked && !isEntitled(it, entitlements))).map((it) => (
                      (entitled && isEntitled(it, entitlements)) ? (
                        <RailLink key={it.key} to={it.path} exact={it.exact} label={it.label} onNavigate={() => setOpen(false)} />
                      ) : (
                        <LockedEntry key={it.key} entry={it} open={lockOpen === it.key}
                                     learn={learnPath(it, entry)} price={priceLine(plansOfGroup(it.group || entry.group))}
                                     isAgency={isAgency} activeId={activeId}
                                     onToggle={() => setLockOpen((k) => (k === it.key ? null : it.key))}
                                     onClose={() => setLockOpen(null)} />
                      )
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {/* Session 36: while they are still being set up this is the only
              thing that matters, so it sits above everything else. */}
          {!isAgency && activeClient?.status === 'onboarding' && (
            <div className="rail-module">
              <RailLink to="/welcome" icon="settings" label="Finish setting up"
                        onNavigate={() => setOpen(false)} />
            </div>
          )}

          {/* Session 55: an active client can come back to their details any time */}
          {!isAgency && activeClient && activeClient.status !== 'onboarding' && (
            <div className="rail-module">
              <RailLink to="/welcome" icon="settings" label="Your details" onNavigate={() => setOpen(false)} />
            </div>
          )}

          {isAgency && activeId && (
            <>
              <div className="rail-divider" role="separator" />
              {/* Session 55: every client on one page */}
              <div className="rail-module">
                <RailLink to="/admin/clients" exact icon="settings" label="Clients" tag="agency" onNavigate={() => setOpen(false)} />
              </div>
              <div className="rail-module">
                <RailLink to={`/admin/clients/${activeId}/settings`} icon="settings" label="Settings" tag="agency" onNavigate={() => setOpen(false)} />
              </div>
              {/* Session 16: ad hoc blogs, agency only (never in a client's rail) */}
              <div className="rail-module">
                <RailLink to={`/admin/clients/${activeId}/adhoc`} icon="blogs" label="Ad hoc blogs" tag="agency" onNavigate={() => setOpen(false)} />
              </div>
              {/* Session 33: sign-ups are agency-wide, not per client, so this
                  one link has no client in it. */}
              <div className="rail-module">
                <RailLink to="/admin/signups" icon="settings" label="Sign-ups" tag="agency" onNavigate={() => setOpen(false)} />
              </div>
            </>
          )}
        </nav>

        <div className="rail-foot">
          <div className="who">
            {me?.display_name || activeClient?.name || 'Signed in'}
            {isAgency && <span className="role">agency</span>}
          </div>
          <button type="button" onClick={signOut}>Sign out</button>
        </div>
      </aside>
    </>
  );
}

function RailLink({ to, exact, icon, label, badge, tag, onNavigate }) {
  const withClient = useClientPath();
  return (
    <NavLink to={withClient(to)} end={!!exact} onClick={onNavigate} className={icon ? 'rail-top-link' : undefined}>
      {({ isActive }) => (
        <span className={`rail-link-inner ${isActive ? 'active-inner' : ''}`}>
          {icon && <span className="rail-icon">{ICON[icon]}</span>}
          <span className="rail-label">{label}</span>
          {badge > 0 && <span className="rail-badge">{badge}</span>}
          {tag && <span className="rail-tag">{tag}</span>}
        </span>
      )}
    </NavLink>
  );
}

// A locked entry: muted text, a padlock, keyboard-focusable. Clicking opens a
// small popover with the description, the price and a Learn more link (Session
// 49: the way an existing client adds a service; before, it asked them to
// email Leena). The agency sees the switch instead. Escape or clicking
// elsewhere closes it.
function LockedEntry({ entry, icon, open, onToggle, onClose, labelOverride, learn, price, isAgency, activeId }) {
  const withClient = useClientPath();
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open, onClose]);
  const label = labelOverride || entry.label;
  return (
    <div className="rail-locked-wrap" ref={ref}>
      <button type="button" className={`rail-locked ${icon ? 'rail-module-header' : ''}`} aria-expanded={open}
              aria-haspopup="dialog" onClick={onToggle}>
        {icon && <span className="rail-icon">{ICON[icon]}</span>}
        <span className="rail-label">{label}</span>
        {LOCK}
      </button>
      {open && (
        <div className="lock-popover" role="dialog" aria-label={`${entry.label}: not in your plan yet`}>
          <div className="lock-popover-title">{entry.label}</div>
          {entry.description && <p>{entry.description}</p>}
          <p>{ASK_LINE}{price ? ` ${price}` : ''}</p>
          {learn && !isAgency && (
            <NavLink to={withClient(learn)} onClick={onClose}>{LEARN_LINE}</NavLink>
          )}
          {learn && isAgency && activeId && (
            <NavLink to={`/admin/clients/${activeId}/settings?client=${activeId}`} onClick={onClose}>Switch it on in Settings</NavLink>
          )}
        </div>
      )}
    </div>
  );
}
